import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';

import { GBENSContext } from '../GBENSContainer';

import Row from './_Row';

const SimpleTextInput = ({
  id,
  screen,
  modal,
  placeholder,
  consignment,
  item,
  name,
  type,
  maxLength,
  weight,
  toolTip,
  disabled,
  localErrors,
  rowProps,
}) => {
  const {
    currentConsignmentErrors,
    currentConsignmentHMRCErrors,
    currentConsignment,
    data,
    updateData,
    errors,
    errorsHMRC,
    errorsLocal,
    updateCurrentConsignment,
    updateCurrentLine,
    currentLine,
  } = useContext(GBENSContext);

  const [localValue, setLocalValue] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  //Calculate Error
  const modalGlobal = modal?.startsWith?.('_') ? modal.slice(1, modal.length) : modal;
  const _error = localErrors
    ? (errorsLocal?.[modalGlobal]?.[id] ?? null)
    : item
      ? (currentConsignmentErrors?.lines?.find?.((l) => l.id === currentLine.id)?.[id] ?? null)
      : consignment
        ? modal
          ? (currentConsignmentErrors?.[modalGlobal]?.[id] ?? null)
          : (currentConsignmentErrors?.[id] ?? null)
        : modal
          ? (errors?.[screen]?.[modalGlobal]?.[id] ?? null)
          : (errors?.[screen]?.[id] ?? null);

  const _hmrError = item
    ? (currentConsignmentHMRCErrors?.lines?.find?.((l) => l.id === currentLine.id)?.[id] ?? null)
    : errorsHMRC?.[screen]?.[id];

  //Calculate Value
  const value = item
    ? (currentLine?.[id] ?? '')
    : consignment
      ? modal
        ? (currentConsignment?.[modal]?.[id] ?? '')
        : (currentConsignment?.[id] ?? '')
      : modal
        ? (data?.[screen]?.[modal]?.[id] ?? '')
        : (data?.[screen]?.[id] ?? '');

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    let newValue = e.target.value;

    // Special character validation ONLY for "sealNumber"
    if (id === 'sealNumber') {
      const validValue = newValue.replace(/[^a-zA-Z0-9 ]/g, ''); // Remove special characters

      // If invalid characters are removed, show error
      if (newValue !== validValue) {
        setErrorMessage('Special characters are not permitted.');
      } else {
        setErrorMessage(''); // Clear error message
      }

      newValue = validValue;
    }

    // Commodity code validation - only allow integers, max 10 digits
    if (id === 'commodityCode') {
      // Remove any non-digit characters
      const validValue = newValue.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedValue = validValue.slice(0, 10);

      // Show error if invalid characters were removed
      if (newValue !== limitedValue && newValue !== validValue) {
        setErrorMessage('Only integers are allowed in commodity code.');
      } else {
        setErrorMessage('');
      }

      newValue = limitedValue;
    }

    setLocalValue(newValue); // Update input value
  };

  //Calculate onChange function
  const handleDataChange = (e) => {
    item
      ? updateCurrentLine(e, id)
      : consignment
        ? updateCurrentConsignment(e, id, modal)
        : updateData(e, screen, id, modal);
  };

  const handleBlurTrimmed = (e) => {
    const newValue = e.target.value.trim();
    handleDataChange({ target: { ...e.target, value: newValue } });
  };

  const handlePaste = (e) => {
    // Only handle paste for commodityCode
    if (id === 'commodityCode') {
      e.preventDefault();
      
      // Get pasted text
      const pastedText = e.clipboardData.getData('text');
      
      // Remove all non-digit characters
      const digitsOnly = pastedText.replace(/\D/g, '');
      
      // Limit to 10 digits
      const limitedValue = digitsOnly.slice(0, 10);
      
      // Update the local value
      setLocalValue(limitedValue);
      
      // Clear any error messages
      setErrorMessage('');
    }
  };

  return (
    <Row
      name={name}
      warning={_error}
      hmrcWarning={_hmrError}
      toolTip={toolTip}
      rowProps={rowProps}
      component={
        <FormControl variant="standard" fullWidth>
          <TextField
            fullWidth
            type={type}
            id={id}
            onBlur={handleBlurTrimmed}
            onPaste={handlePaste}
            value={localValue}
            placeholder={placeholder}
            error={!!errorMessage} // ✅ Show error
            helperText={errorMessage} // ✅ Display error message
            disabled={disabled}
            slotProps={{
              inputLabel: {
                shrink: false,
                style: { color: '#00857d', margin: 0, padding: 0, fontSize: 15, fontStyle: 'italic' },
              },
              input: {
                style: {
                  backgroundColor: '#ffffff',
                },
                ...(weight
                  ? {
                      endAdornment: <InputAdornment>Kg</InputAdornment>,
                      type: 'number',
                      inputProps: { min: 0, step: 'any' },
                    }
                  : {}),
              },
              htmlInput: { maxLength },
            }}
            onChange={handleChange}
            autoComplete="off"
            sx={{
              '& .MuiInput-underline:before': {
                borderBottom: 'none !important', // Ensures underline is removed before focus
              },
              '& .MuiInput-underline:hover:before': {
                borderBottom: 'none !important', // Ensures underline is removed on hover
              },
              '& .MuiInput-underline:after': {
                borderBottom: 'none !important', // Ensures underline is removed after focus
              },
            }}
          />
        </FormControl>
      }
    />
  );
};

SimpleTextInput.propTypes = {
  placeholder: PropTypes.string,
  id: PropTypes.string,
  screen: PropTypes.string,
  modal: PropTypes.string,
  consignment: PropTypes.bool,
  item: PropTypes.bool,
  name: PropTypes.string,
  type: PropTypes.string,
  maxLength: PropTypes.number,
  toolTip: PropTypes.string,
  rowProps: PropTypes.object,
  weight: PropTypes.bool,
  disabled: PropTypes.bool,
  localErrors: PropTypes.bool,
};

export default SimpleTextInput;
